import { useState, useEffect, useCallback } from 'react';
import { Pizza, Pizzeria } from '../../types';
import { useFirestore } from '../../hooks/useFirestore';
import { useMenuPizzas } from '../../hooks/useMenuPizzas';
import { useSlices } from '../../hooks/useSlices';
import { Switch } from '@headlessui/react';
import Modal from '../Modal';

interface PizzeriaMenuManagerProps {
  pizzeria: Pizzeria;
  onUpdate: () => void;
  onSliceOperation: () => void;
}

export default function PizzeriaMenuManager({ pizzeria, onUpdate, onSliceOperation }: PizzeriaMenuManagerProps) {
  const { searchPizzas } = useFirestore();
  const { menuPizzas, loading, error, addPizzaToMenu, removePizzaFromMenu, togglePizzaVisibility, reloadMenu } = useMenuPizzas(pizzeria.id);
  const { sliceBalance, useSliceForPizza, refundSlice, reloadBalance } = useSlices();
  const [availablePizzas, setAvailablePizzas] = useState<Pizza[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedPizza, setSelectedPizza] = useState<Pizza | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<{
    menuItemId: string;
    daysRemaining: number;
    pizzaName: string;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const loadAvailablePizzas = useCallback(async () => {
    try {
      const allPizzas = await searchPizzas({ type: 'pizza', query: '' });
      const menuPizzaIds = menuPizzas.map(item => item.pizza.id);
      const available = allPizzas.filter(p => !menuPizzaIds.includes(p.id));
      setAvailablePizzas(available);
    } catch (error) {
      console.error('Errore nel caricamento delle pizze disponibili:', error);
    }
  }, [searchPizzas, menuPizzas]);

  useEffect(() => {
    loadAvailablePizzas();
  }, [loadAvailablePizzas]);

  const handleAddPizza = async (pizza: Pizza) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setSelectedPizza(pizza);

    try {
      await reloadBalance();
      if (sliceBalance <= 0) {
        setShowErrorModal(true);
        return;
      }

      const sliceUsed = await useSliceForPizza();
      if (!sliceUsed) {
        setShowErrorModal(true);
        return;
      }

      await addPizzaToMenu(pizza.id);
      setShowAddModal(false);
      // Per l'aggiunta usiamo l'evento
      window.dispatchEvent(new Event('sliceBalanceUpdate'));
      await reloadMenu();
      await loadAvailablePizzas();
    } catch (error) {
      console.error('Errore nell\'aggiunta della pizza:', error);
      setShowErrorModal(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveClick = (menuItemId: string, daysRemaining: number, pizzaName: string) => {
    setSelectedMenuItem({ menuItemId, daysRemaining, pizzaName });
    setShowRemoveModal(true);
  };

  const handleRemovePizza = async () => {
    if (!selectedMenuItem || isProcessing) return;
    setIsProcessing(true);
    console.log('Inizio rimozione pizza');
  
    try {
      // Prima rimuoviamo la pizza
      await removePizzaFromMenu(selectedMenuItem.menuItemId);
      console.log('Pizza rimossa dal menu');
      
      // Poi facciamo il refund se necessario
      if (selectedMenuItem.daysRemaining > 0) {
        console.log('Tentativo di rimborso spicchio');
        const refunded = await refundSlice();
        console.log('Risultato rimborso:', refunded);
        if (refunded) {
          // Usiamo solo l'evento, non onSliceOperation
          window.dispatchEvent(new Event('sliceBalanceUpdate'));
        }
      }
      
      setShowRemoveModal(false);
      await reloadMenu();
      await loadAvailablePizzas();
    } catch (error) {
      console.error('Errore nella rimozione della pizza:', error);
    } finally {
      setIsProcessing(false);
      setSelectedMenuItem(null);
    }
  };
  if (loading) {
    return <div>Caricamento menu...</div>;
  }

  if (error) {
    return <div className="text-red-600">Errore nel caricamento del menu: {error}</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold">Menu Pizze</h4>
        <button
          onClick={() => setShowAddModal(true)}
          className="text-pizzaveg hover:text-pizzaveg-dark"
        >
          + Aggiungi Pizza
        </button>
      </div>

      <div className="space-y-4">
        {menuPizzas.map(({ pizza, daysRemaining, menuItemId, isHidden }) => (
          <div key={menuItemId} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
            <div>
              <h5 className="font-medium">{pizza.name}</h5>
              <p className="text-sm text-gray-600">{pizza.ingredients.join(', ')}</p>
              <p className="text-sm text-pizzaveg mt-1">
                {daysRemaining > 0 ? `${daysRemaining} giorni rimanenti` : 'Scaduta'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Switch
                checked={!isHidden}
                onChange={(checked) => togglePizzaVisibility(menuItemId, !checked)}
                className={`${
                  !isHidden ? 'bg-pizzaveg' : 'bg-gray-300'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
              >
                <span
                  className={`${
                    !isHidden ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <button
                onClick={() => handleRemoveClick(menuItemId, daysRemaining, pizza.name)}
                className="text-red-600 hover:text-red-700"
                disabled={isProcessing}
              >
                Rimuovi
              </button>
            </div>
          </div>
        ))}

        {menuPizzas.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nessuna pizza nel menu. Aggiungi la tua prima pizza!
          </div>
        )}
      </div>

      {showAddModal && (
        <Modal onClose={() => !isProcessing && setShowAddModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Aggiungi Pizza al Menu</h3>
            {sliceBalance <= 0 ? (
              <div className="text-center py-4">
                <p className="text-red-600 mb-2">
                  Non hai spicchi sufficienti per aggiungere pizze al menu.
                </p>
                <p className="text-gray-600">
                  Acquista degli spicchi dalla sezione "Box Spicchi" per continuare.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availablePizzas.map(pizza => (
                  <div key={pizza.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div>
                      <h5 className="font-medium">{pizza.name}</h5>
                      <p className="text-sm text-gray-600">{pizza.ingredients.join(', ')}</p>
                    </div>
                    <button
                      onClick={() => handleAddPizza(pizza)}
                      className="text-pizzaveg hover:text-pizzaveg-dark"
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Aggiunta...' : 'Aggiungi'}
                    </button>
                  </div>
                ))}
                {availablePizzas.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    Non ci sono pizze disponibili da aggiungere al menu.
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-600 hover:text-gray-800"
                disabled={isProcessing}
              >
                Chiudi
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showErrorModal && (
        <Modal onClose={() => setShowErrorModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2">Spicchi insufficienti</h3>
            <p className="text-gray-600 mb-4">
              Non hai abbastanza spicchi per aggiungere questa pizza al menu. 
              Acquista degli spicchi dalla sezione "Box Spicchi" per continuare.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 text-pizzaveg hover:text-pizzaveg-dark"
              >
                Chiudi
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showRemoveModal && selectedMenuItem && (
        <Modal onClose={() => !isProcessing && setShowRemoveModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2">Rimuovi pizza dal menu</h3>
            <p className="text-gray-600 mb-4">
              Sei sicuro di voler rimuovere "{selectedMenuItem.pizzaName}" dal menu?
              {selectedMenuItem.daysRemaining > 0 && (
                <span className="block mt-2 text-sm">
                  Nota: rimuovendo questa pizza ora, riceverai indietro uno spicchio 
                  poiché ha ancora {selectedMenuItem.daysRemaining} giorni di validità.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowRemoveModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isProcessing}
              >
                Annulla
              </button>
              <button
                onClick={handleRemovePizza}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={isProcessing}
              >
                {isProcessing ? 'Rimozione...' : 'Rimuovi'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}